/**
 * Rules for one role: knockouts (any hit → Not qualified), scoring (points
 * add up), thresholds (qualified mark, review mark). Order matters and the
 * section copy says so.
 */
import { Plus } from 'lucide-react';
import { RuleErr, RuleRow, ruleInput } from './RuleRow';
import type { KnockoutRule, ScoringRule, ScreeningQuestion, ScreeningRules } from './types';
import type { ConfigErrors } from './validate';

const addBtn = 'inline-flex min-h-10 items-center gap-1.5 rounded-md border border-gray-200 px-3 text-[13px] font-medium text-gray-800 hover:bg-page';

export function RulesEditor({
  rules, questions, errors, onChange,
}: {
  rules: ScreeningRules; questions: ScreeningQuestion[]; errors: ConfigErrors; onChange: (next: ScreeningRules) => void;
}) {
  const setKnockout = (i: number, patch: Partial<KnockoutRule>) =>
    onChange({ ...rules, knockouts: rules.knockouts.map((k, j) => (j === i ? { ...k, ...patch } : k)) });
  const setScoring = (i: number, patch: Partial<ScoringRule>) =>
    onChange({ ...rules, scoring: rules.scoring.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const noQuestions = questions.filter((q) => q.key).length === 0;

  return (
    <section aria-labelledby="rules-h" className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <h2 id="rules-h" className="text-[15px] font-semibold text-gray-900">Rules</h2>
      <p className="mt-0.5 text-[13px] text-muted">
        Checked in this order: a knockout ends it as Not qualified; otherwise points add up and the two marks decide. A missing required answer always lands on Needs review.
      </p>
      {noQuestions && <p className="mt-2 text-[13px] text-warning-text">Add a question with a key first — rules point at questions.</p>}

      <h3 className="mt-4 text-[13px] font-semibold text-gray-900">Knockouts</h3>
      {rules.knockouts.length === 0 ? (
        <p className="mt-1 text-[13px] text-muted">None. Every candidate goes on to scoring.</p>
      ) : (
        <ol className="mt-2 space-y-2">
          {rules.knockouts.map((k, i) => (
            <RuleRow key={i} label="Knockout" index={i} questions={questions} field={k.field} op={k.op} value={k.value} errors={errors.knockouts[i]} onChange={(p) => setKnockout(i, p)} onRemove={() => onChange({ ...rules, knockouts: rules.knockouts.filter((_, j) => j !== i) })}>
              {({ id }) => (
                <div className="text-[13px]">
                  <label htmlFor={`${id}-reason`} className="font-medium text-gray-800">Reason shown on the card</label>
                  <input id={`${id}-reason`} value={k.reason} onChange={(e) => setKnockout(i, { reason: e.target.value })} placeholder="Under 2 years of experience" aria-invalid={errors.knockouts[i]?.reason ? true : undefined} aria-describedby={errors.knockouts[i]?.reason ? `${id}-reason-err` : undefined} className={ruleInput} />
                  <RuleErr id={`${id}-reason-err`} text={errors.knockouts[i]?.reason} />
                </div>
              )}
            </RuleRow>
          ))}
        </ol>
      )}
      <button type="button" disabled={noQuestions} onClick={() => onChange({ ...rules, knockouts: [...rules.knockouts, { field: questions[0]?.key ?? '', op: 'falsy', value: null, reason: '' }] })} className={`${addBtn} mt-2 disabled:opacity-50`}>
        <Plus size={14} strokeWidth={2} aria-hidden /> Add knockout
      </button>

      <h3 className="mt-5 text-[13px] font-semibold text-gray-900">Scoring</h3>
      {rules.scoring.length === 0 ? (
        <p className="mt-1 text-[13px] text-muted">None. Every candidate who clears the knockouts scores 0.</p>
      ) : (
        <ol className="mt-2 space-y-2">
          {rules.scoring.map((s, i) => (
            <RuleRow key={i} label="Scoring rule" index={i} questions={questions} field={s.field} op={s.op} value={s.value} errors={errors.scoring[i]} onChange={(p) => setScoring(i, p)} onRemove={() => onChange({ ...rules, scoring: rules.scoring.filter((_, j) => j !== i) })}>
              {({ id }) => (
                <div className="text-[13px] sm:w-40">
                  <label htmlFor={`${id}-points`} className="font-medium text-gray-800">Points</label>
                  <input id={`${id}-points`} type="number" inputMode="numeric" step="1" value={Number.isFinite(s.points) ? s.points : ''} onChange={(e) => setScoring(i, { points: e.target.value === '' ? NaN : Number(e.target.value) })} aria-invalid={errors.scoring[i]?.points ? true : undefined} aria-describedby={errors.scoring[i]?.points ? `${id}-points-err` : undefined} className={ruleInput} />
                  <RuleErr id={`${id}-points-err`} text={errors.scoring[i]?.points} />
                </div>
              )}
            </RuleRow>
          ))}
        </ol>
      )}
      <button type="button" disabled={noQuestions} onClick={() => onChange({ ...rules, scoring: [...rules.scoring, { field: questions[0]?.key ?? '', op: 'truthy', value: null, points: 1 }] })} className={`${addBtn} mt-2 disabled:opacity-50`}>
        <Plus size={14} strokeWidth={2} aria-hidden /> Add scoring rule
      </button>

      <h3 className="mt-5 text-[13px] font-semibold text-gray-900">Marks</h3>
      <div className="mt-2 grid gap-3 sm:grid-cols-2">
        <div className="text-[13px]">
          <label htmlFor="qualified-min" className="font-medium text-gray-800">Qualified at</label>
          <input id="qualified-min" type="number" inputMode="numeric" step="1" value={Number.isFinite(rules.qualified_min) ? rules.qualified_min : ''} onChange={(e) => onChange({ ...rules, qualified_min: e.target.value === '' ? NaN : Number(e.target.value) })} aria-invalid={errors.qualified_min ? true : undefined} aria-describedby={errors.qualified_min ? 'qualified-min-err' : 'qualified-min-hint'} className={ruleInput} />
          {errors.qualified_min ? <RuleErr id="qualified-min-err" text={errors.qualified_min} /> : <span id="qualified-min-hint" className="mt-1 block text-[12px] text-muted">This many points or more → Qualified.</span>}
        </div>
        <div className="text-[13px]">
          <label htmlFor="review-min" className="font-medium text-gray-800">Review at</label>
          <input id="review-min" type="number" inputMode="numeric" step="1" value={Number.isFinite(rules.review_min) ? rules.review_min : ''} onChange={(e) => onChange({ ...rules, review_min: e.target.value === '' ? NaN : Number(e.target.value) })} aria-invalid={errors.review_min ? true : undefined} aria-describedby={errors.review_min ? 'review-min-err' : 'review-min-hint'} className={ruleInput} />
          {errors.review_min ? <RuleErr id="review-min-err" text={errors.review_min} /> : <span id="review-min-hint" className="mt-1 block text-[12px] text-muted">From here up to the qualified mark → Needs review. Below → Not qualified.</span>}
        </div>
      </div>
    </section>
  );
}
